import React, { useState, useEffect, useRef } from 'react'
import { motion, useMotionValue, animate, useMotionTemplate, AnimatePresence } from 'framer-motion'
import { Wand2, Layers, Palette, ChevronRight, Zap, Upload, Sparkles as SparklesIcon, Check, Scan, Grid as GridIcon, Download, XCircle, Image as ImageIcon, Cpu, Coins, ShieldCheck, ArrowDown, Users } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/SupabaseAuthContext'
import { supabase } from '../../config/supabase'
import { telemetry } from '../../services/telemetryService'
import { toast } from 'sonner'
import LoginModal from '../auth/LoginModal'

// Images
import montadorLogo from '../../assets/images/Logo-Montador.png'
import melhoradorCloudLogo from '../../assets/images/logo-melhorador-cloud.png'
import melhoradorDesktopLogo from '../../assets/images/logo-dtf-spot-white.png'
import dtfFactoryLogo from '../../assets/images/DTF factory logo.png'

// --- DATA ---
const CARDS = [
    {
        id: 1,
        title: "Melhorador Cloud",
        shortTitle: "MELHORADOR DE IMAGEM",
        icon: melhoradorCloudLogo,
        bgImage: "https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?q=80&w=2070&auto=format&fit=crop",
        visualImage: "/Melhorar imagem.jpg",
        desc: "IA processa via nuvem e entrega Melhorada 300dpi perfeito. Resultado garantido em menos de 3 segundos.",
        tech: "IA CLOUD",
        color: "#06b6d4", // Cyan 500
        themeColor: "cyan",
        step: "01",
        visual: "scan",
        chatPrompt: "Melhore esta imagem em 4x usando processamento em nuvem",
        features: ["Sem GPU Necessária", "Acesso Mobile/Web", "Alta Velocidade"],
        path: "/software/melhorador-cloud",
        logo: melhoradorCloudLogo,
        ctaLabel: "MELHORAR AGORA"
    },
    {
        id: 2,
        title: "Spot White",
        shortTitle: "SPOT WHITE",
        icon: melhoradorDesktopLogo,
        bgImage: "https://images.unsplash.com/photo-1550684848-fac1c5b4e853?q=80&w=2070&auto=format&fit=crop",
        desc: "Contração inteligente, Opções rapidas , exportação direta pro RIP. Sempre perfeito.",
        tech: "DTF",
        color: "#f8fafc", // Slate 50
        themeColor: "white",
        step: "02",
        visual: "layer",
        chatPrompt: "Gere o canal de branco para esta estampa com fundo transparente",
        features: ["Base Automática", "Contração de Borda", "Economia de Tinta"],
        path: "/software/spot-white",
        logo: melhoradorDesktopLogo,
        ctaLabel: "PROCESSAR AGORA"
    },
    {
        id: 3,
        title: "DTF Factory",
        shortTitle: "DTF FACTORY",
        icon: dtfFactoryLogo,
        bgImage: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070&auto=format&fit=crop",
        desc: "Já imaginou Gerar imagens prontas para impressão DTF ? Sem Fundo e em 300 DPI no tamanho que você escolher ? Saiba mais clicando aqui",
        tech: "GERADOR",
        color: "#a855f7", // Purple 500
        themeColor: "purple",
        step: "03",
        visual: "process",
        chatPrompt: "Crie uma estampa vetorial de um samurai cyberpunk",
        features: ["~50s Geração", "300 DPI", "100% Exclusivo", "PNG sem fundo"],
        path: "/software/dtf-fabart",
        logo: dtfFactoryLogo,
        ctaLabel: "COMEÇAR A CRIAR"
    },
    {
        id: 4,
        title: "Montador Automático",
        shortTitle: "MONTADOR",
        icon: montadorLogo,
        bgImage: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop",
        desc: "Otimização automática de layout para economia de filme e tempo.",
        tech: "AUTOMÁTICO",
        color: "#3b82f6", // Blue
        themeColor: "blue",
        step: "04",
        visual: "blocks",
        chatPrompt: "Organize estas imagens para economizar o máximo de espaço",
        features: ["Auto Nesting", "Marcas de Corte", "Relatórios"],
        path: "/software/montador",
        logo: montadorLogo,
        ctaLabel: "ORGANIZAR"
    }
]

// --- VISUAL COMPONENTS ---

function VisualScan({ color, image }: { color: string, image: string }) {
    return (
        <div className="absolute inset-0 overflow-hidden rounded-r-xl bg-black flex flex-col items-center justify-center group">
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 mix-blend-overlay"></div>
            
            {/* Watermark */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
                 <span className="text-[120px] font-black text-white/5 -rotate-12 select-none whitespace-nowrap">UPSCALE</span>
            </div>

            <div className="relative w-full h-full overflow-hidden bg-black shadow-2xl">
                {/* Before Label */}
                <div className="absolute top-4 left-4 z-20 bg-black/60 backdrop-blur-md px-3 py-1 rounded border border-white/10 text-[10px] font-bold text-white/50">ORIGINAL</div>
                
                {/* Original Image (Pixelated Low Res) */}
                <img 
                    src={image} 
                    className="absolute inset-0 w-full h-full object-cover opacity-80 scale-105" 
                    style={{ filter: 'blur(4px)' }}
                    alt="Original Low Res" 
                />
                
                {/* Scan Line & After Reveal */}
                <div className="absolute inset-0 animate-[scan_4s_ease-in-out_infinite] border-b-[3px] border-cyan-400 bg-cyan-500/5 shadow-[0_10px_40px_rgba(6,182,212,0.3)] backdrop-blur-[1px] z-10">
                   <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-cyan-400 shadow-[0_0_20px_cyan,0_0_40px_cyan]"></div>
                   <div className="absolute bottom-2 right-2 text-[10px] font-bold text-cyan-300 bg-black/50 px-2 py-0.5 rounded backdrop-blur-sm border border-cyan-500/30">AI PROCESSING</div>
                </div>

                {/* Revealed High Res Image */}
                <div className="absolute inset-0 animate-[reveal_4s_ease-in-out_infinite] overflow-hidden z-0">
                     <img 
                        src={image} 
                        className="w-full h-full object-cover saturate-150 contrast-110 sharpen" 
                        alt="Enhanced" 
                     />
                     {/* After Label */}
                     <div className="absolute top-4 right-4 z-20 bg-cyan-500/90 backdrop-blur-md px-3 py-1 rounded border border-white/20 text-[10px] font-bold text-white shadow-lg shadow-cyan-500/20">4X ULTRA</div>
                </div>
            </div>

            <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-4 text-xs font-mono text-cyan-400 z-30 pointer-events-none">
                <div className="flex flex-col items-center bg-black/40 p-2 rounded backdrop-blur-sm">
                    <span className="text-white/40 text-[10px]">RESOLUTION</span>
                    <span className="font-bold">4096px</span>
                </div>
                <div className="flex flex-col items-center bg-black/40 p-2 rounded backdrop-blur-sm">
                    <span className="text-white/40 text-[10px]">DPI</span>
                    <span className="font-bold">300 PPI</span>
                </div>
                <div className="flex flex-col items-center bg-black/40 p-2 rounded backdrop-blur-sm">
                    <span className="text-white/40 text-[10px]">SPEED</span>
                    <span className="font-bold">0.8s</span>
                </div>
            </div>

            <style>{`
                @keyframes scan {
                    0%, 100% { height: 0%; opacity: 0; }
                    10% { opacity: 1; }
                    50% { height: 100%; opacity: 1; }
                    90% { opacity: 1; }
                }
                @keyframes reveal {
                    0%, 100% { clip-path: inset(0 0 100% 0); }
                    50% { clip-path: inset(0 0 0 0); }
                }
             `}</style>
        </div>
    )
}

function VisualLayer({ color }: { color: string }) {
    return (
        <div className="absolute inset-0 overflow-hidden rounded-r-xl bg-black/50 flex flex-col items-center justify-center group">
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 mix-blend-overlay"></div>
            
            {/* Watermark */}
             <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
                 <span className="text-[120px] font-black text-white/5 -rotate-12 select-none whitespace-nowrap">DESKTOP</span>
            </div>

            <div className="relative w-full h-full border border-white/10 rounded-xl overflow-hidden bg-black/20 shadow-2xl m-8">
                
                {/* Imagem Original (Mewtwo PNG) */}
                <img 
                    src="https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/150.png" 
                    className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_0_15px_rgba(168,85,247,0.5)] z-0 p-8" 
                    alt="Original Color" 
                />

                {/* Camada Melhorador Desktop (Cópia Branca com Recorte) */}
                <div className="absolute inset-0 z-10 animate-[scanHorizontal_4s_ease-in-out_infinite] overflow-hidden p-8">
                    <img 
                        src="https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/150.png" 
                        className="w-full h-full object-contain brightness-[100] contrast-[100] grayscale opacity-100 drop-shadow-[0_0_10px_rgba(255,255,255,0.8)]" 
                        alt="Melhorador Desktop Channel" 
                        style={{ filter: 'brightness(0) invert(1)' }}
                    />
                    {/* Linha de Scan Lateral */}
                    <div className="absolute top-0 right-0 w-[2px] h-full bg-white shadow-[0_0_20px_white] box-shadow-[0_0_40px_white]"></div>
                </div>

                 {/* Label Flutuante */}
                 <div className="absolute bottom-10 right-0 bg-white text-black px-3 py-1 rounded-l-lg font-bold text-xs shadow-xl flex items-center gap-2 z-20">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                    DESKTOP GENERATED
                </div>

            </div>

            {/* Stats */}
            <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-4 text-xs font-mono text-gray-400 pointer-events-none">
                <div className="flex items-center gap-2 bg-black/40 px-3 py-1 rounded backdrop-blur-sm">
                    <Zap className="w-3 h-3 text-yellow-400" />
                    <span>AUTO-MASK</span>
                </div>
                <div className="w-px h-4 bg-white/10"></div>
                <div className="bg-black/40 px-3 py-1 rounded backdrop-blur-sm">100% WHITE</div>
            </div>

            <style>{`
                @keyframes scanHorizontal {
                    0%, 100% { clip-path: inset(0 100% 0 0); }
                    50% { clip-path: inset(0 0 0 0); }
                }
             `}</style>
        </div>
    )
}

function VisualPixelMaterialize({ color }: { color: string }) {
    // Generate grid for pixel effect
    const gridSize = 12;
    const blocks = Array.from({ length: gridSize * gridSize });
    
    const [isHovered, setIsHovered] = useState(false);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setMousePos({ x, y });
    };

    return (
        <div className="absolute inset-0 overflow-hidden rounded-r-xl bg-black/50 flex flex-col items-center justify-center group">
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 mix-blend-overlay"></div>
            
             {/* Watermark */}
             <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
                 <span className="text-[120px] font-black text-purple-500/5 -rotate-12 select-none whitespace-nowrap">GENERATE</span>
            </div>

            <div 
                className="relative w-full h-full bg-black border border-purple-500/30 rounded-xl overflow-hidden shadow-[0_0_50px_rgba(168,85,247,0.2)] cursor-crosshair"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onMouseMove={handleMouseMove}
            >
                {/* Final Image */}
                <img 
                    src="/exemplo reticula spiral.png" 
                    className="absolute inset-0 w-full h-full object-contain p-4 transition-transform duration-200 ease-out"
                    style={{
                        transform: isHovered ? 'scale(4)' : 'scale(1)',
                        transformOrigin: `${mousePos.x}% ${mousePos.y}%`
                    }}
                    alt="Generated Art"
                />

                {/* Pixel Grid Mask */}
                <div className={`absolute inset-0 grid grid-cols-12 grid-rows-12 z-10 pointer-events-none transition-opacity duration-300 ${isHovered ? 'opacity-0' : 'opacity-100'}`}>
                    {blocks.map((_, i) => (
                        <motion.div
                            key={i}
                            className="bg-black border-[0.5px] border-purple-900/20"
                            initial={{ opacity: 1, scale: 1 }}
                            animate={{ opacity: 0, scale: 0.5 }}
                            transition={{
                                duration: 2,
                                delay: Math.random() * 2,
                                repeat: Infinity,
                                repeatType: "reverse",
                                repeatDelay: 3,
                                ease: "easeInOut"
                            }}
                        />
                    ))}
                </div>

                {/* Scanning Effect Overlay */}
                <div className={`absolute inset-0 z-20 pointer-events-none mix-blend-screen transition-opacity duration-300 ${isHovered ? 'opacity-0' : 'opacity-100'}`}>
                    <div className="w-full h-[2px] bg-purple-500 shadow-[0_0_20px_#a855f7] animate-[scanFast_4s_linear_infinite]"></div>
                </div>

                {/* UI Overlay */}
                <div className={`absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black via-black/80 to-transparent z-30 flex justify-between items-end transition-opacity duration-300 ${isHovered ? 'opacity-0' : 'opacity-100'}`}>
                    <div className="flex flex-col">
                        <span className="text-[10px] text-purple-300 font-mono mb-1">PROMPT</span>
                        <span className="text-sm font-bold text-white leading-tight">Leão de Fogo Chinês <br/> Spiral Halftone</span>
                    </div>
                    <div className="flex gap-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-bounce shadow-[0_0_5px_#a855f7]"></div>
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-bounce delay-75 shadow-[0_0_5px_#a855f7]"></div>
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-bounce delay-150 shadow-[0_0_5px_#a855f7]"></div>
                    </div>
                </div>
                
                {/* Processing Badge */}
                <div className={`absolute top-4 right-4 bg-purple-600/90 text-white text-[10px] font-bold px-2 py-1 rounded shadow-lg border border-white/20 backdrop-blur-md animate-pulse transition-opacity duration-300 ${isHovered ? 'opacity-0' : 'opacity-100'}`}>
                    AI PROCESSING
                </div>
                
                {/* Zoom Badge - Show on Hover */}
                 <div className={`absolute top-4 right-4 bg-purple-600/90 text-white text-[10px] font-bold px-2 py-1 rounded shadow-lg border border-white/20 backdrop-blur-md transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
                    ZOOM X4
                </div>
            </div>

            <style>{`
                @keyframes scanFast {
                    0% { transform: translateY(-100%); opacity: 0; }
                    10% { opacity: 1; }
                    90% { opacity: 1; }
                    100% { transform: translateY(400px); opacity: 0; }
                }
            `}</style>
        </div>
    )
}

function VisualBlocks({ color }: { color: string }) {
    const images = [
        "https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?q=80&w=200&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=200&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1550684848-fac1c5b4e853?q=80&w=200&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?q=80&w=200&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1563089145-599997674d42?q=80&w=200&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?q=80&w=200&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1535905557558-afc4877a26fc?q=80&w=200&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1618331835717-801e976710b2?q=80&w=200&auto=format&fit=crop"
    ]

    return (
        <div className="absolute inset-0 overflow-hidden rounded-r-xl bg-black/50 flex flex-col items-center justify-center">
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 mix-blend-overlay"></div>
            
             {/* Watermark */}
             <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
                 <span className="text-[120px] font-black text-blue-500/5 -rotate-12 select-none whitespace-nowrap">NESTING</span>
            </div>

            <div className="relative w-full h-full border-2 border-dashed border-blue-500/30 rounded-xl p-3 flex flex-wrap content-start gap-2 bg-blue-900/5 transition-all duration-1000 hover:border-blue-500/60 m-8">
                 <div className="absolute -top-3 left-4 bg-blue-600 px-3 py-0.5 rounded text-[10px] font-bold text-white shadow-lg shadow-blue-500/20">60cm x 100cm</div>
                 
                 {images.map((img, i) => (
                    <motion.div 
                        key={i}
                        className="rounded-lg flex items-center justify-center overflow-hidden relative border border-white/10 shadow-sm"
                        style={{ 
                            width: i % 2 === 0 ? '80px' : '55px', 
                            height: i % 3 === 0 ? '80px' : '55px',
                            flexGrow: Math.random() > 0.7 ? 1 : 0
                        }}
                        initial={{ scale: 0, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        transition={{ 
                            delay: i * 0.1, 
                            duration: 0.6,
                            type: "spring",
                            stiffness: 200
                        }}
                        whileHover={{ scale: 1.05, borderColor: '#3b82f6' }}
                    >
                        <img src={img} className="w-full h-full object-cover opacity-90 hover:opacity-100 transition-opacity" alt={`Block ${i}`} />
                    </motion.div>
                 ))}
                 
                 <motion.div 
                    className="absolute inset-0 bg-blue-500/5 border border-blue-500/30 flex items-center justify-center backdrop-blur-[1px] rounded-xl"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 1, 0] }}
                    transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
                 >
                    <div className="bg-black/80 p-4 rounded-2xl border border-blue-500/50 shadow-[0_0_30px_rgba(59,130,246,0.4)] flex flex-col items-center">
                        <span className="text-blue-400 font-bold tracking-widest text-lg">ECONOMIA</span>
                        <span className="text-3xl font-black text-white">42%</span>
                        <span className="text-xs text-gray-400 mt-1">DE FILME</span>
                    </div>
                 </motion.div>
            </div>
        </div>
    )
}

function VisualParticles({ color }: { color: string }) {
     return (
        <div className="absolute inset-0 overflow-hidden rounded-r-xl bg-black/50 flex flex-col items-center justify-center">
             <SparklesIcon className="w-16 h-16 text-purple-500 animate-pulse" />
        </div>
     )
}


// --- MAIN COMPONENT ---

export default function HeroCarousel2D() {
    const navigate = useNavigate()
    const { user } = useAuth()
    
    // --- LAUNCHER DOWNLOAD LOGIC ---
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
    const [pendingDownload, setPendingDownload] = useState(false)
    const [downloadUrl, setDownloadUrl] = useState('')
    const [launcherVersion, setLauncherVersion] = useState('v2.4.0 Stable')

    const formatDropboxUrl = (url: string) => {
        if (url.includes('dropbox.com')) {
            if (url.includes('dl=0')) return url.replace('dl=0', 'dl=1')
            if (!url.includes('dl=')) return url + (url.includes('?') ? '&dl=1' : '?dl=1')
        }
        return url
    }

    useEffect(() => {
        const fetchLauncherData = async () => {
            try {
                const { data, error } = await supabase
                    .from('software_versions')
                    .select('download_url, latest_version')
                    .eq('id', 'overpixel-launcher')
                    .single()

                if (data && !error) {
                    if (data.download_url) {
                        setDownloadUrl(formatDropboxUrl(data.download_url))
                    }
                    if (data.latest_version) setLauncherVersion(`v${data.latest_version} Stable`)
                }
            } catch (err) {
                console.error('Failed to fetch launcher data:', err)
            }
        }

        fetchLauncherData()
    }, [])

    const executeDownload = () => {
        const finalUrl = downloadUrl || formatDropboxUrl('https://www.dropbox.com/scl/fi/g7x6j3q8k9l2m4n5p0q1r/Overpixel-Launcher-Setup.exe?rlkey=8j9k0l1m2n3o4p5q6r7s8t9u0&dl=1')

        // Track download
        telemetry.track('launcher_download', {
            version: launcherVersion.replace('v', '').replace(' Stable', ''),
            url: finalUrl,
            user_id: user?.id || 'unknown',
            source: 'hero_carousel'
        })

        // Force flush
        telemetry.flush(true)

        // Create a temporary anchor element
        const link = document.createElement('a')
        link.href = finalUrl
        link.setAttribute('download', 'Overpixel-Launcher-Setup.exe')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        toast.success('Download iniciado!')
    }

    const handleDownload = () => {
        if (!user) {
            setPendingDownload(true)
            setIsLoginModalOpen(true)
            return
        }
        executeDownload()
    }

    useEffect(() => {
        if (user && pendingDownload) {
            setPendingDownload(false)
            executeDownload()
        }
    }, [user, pendingDownload])
    // -------------------------------

    const [activeIndex, setActiveIndex] = useState(0)
    const [isPaused, setIsPaused] = useState(false)
    
    // Infinite Carousel Logic
    // We duplicate the array to create an infinite loop illusion
    // const extendedCards = [...CARDS, ...CARDS, ...CARDS] 
    
    // Auto-scroll
    useEffect(() => {
        if (isPaused) return

        const interval = setInterval(() => {
            setActiveIndex(prev => (prev + 1) % CARDS.length)
        }, 10000) // Slower interval (10s)
        return () => clearInterval(interval)
    }, [isPaused])

    const handleDotClick = (index: number) => {
        setActiveIndex(index)
    }

    return (
        <div className="w-full min-h-screen bg-black flex flex-col items-center justify-center relative overflow-hidden py-20">
            
            {/* Dynamic Background Image */}
            <AnimatePresence mode="popLayout">
                <motion.div
                    key={CARDS[activeIndex].id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.4 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.5, ease: "easeInOut" }}
                    className="absolute inset-0 z-0"
                >
                    <img 
                        src={CARDS[activeIndex].bgImage} 
                        alt="" 
                        className="w-full h-full object-cover blur-sm scale-110" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/80"></div>
                </motion.div>
            </AnimatePresence>

            {/* Background Effects */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-transparent to-black/80 z-0 pointer-events-none"></div>
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay z-0 pointer-events-none"></div>
            
            {/* Main Carousel Container */}
            <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 md:px-8">
                
                {/* Header */}
                <motion.div 
                    initial={{ opacity: 0, y: -20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl md:text-6xl font-black text-white mb-4 tracking-tight">
                        Ferramentas Para <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">acelerar sua empresa</span>
                    </h2>
                    <p className="text-gray-400 max-w-2xl mx-auto text-lg">
                        Ferramentas de IA de última geração para revolucionar seu fluxo de trabalho de impressão e design.
                    </p>
                </motion.div>

                {/* Cards Display */}
                <div 
                    className="relative w-full overflow-visible"
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                >
                    <div className="flex flex-col md:flex-row gap-8 items-center justify-center min-h-[600px]">
                        <AnimatePresence mode='wait'>
                            {CARDS.map((card, index) => (
                                index === activeIndex && (
                                    <motion.div 
                                        key={card.id}
                                        initial={{ opacity: 0, scale: 0.9, x: 100 }}
                                        animate={{ opacity: 1, scale: 1, x: 0 }}
                                        exit={{ opacity: 0, scale: 0.9, x: -100 }}
                                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                        className="w-full max-w-5xl bg-[#111] border border-white/10 rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row"
                                    >
                                        {/* Left Content */}
                                        <div className="w-full md:w-1/2 p-8 md:p-12 flex flex-col justify-between relative overflow-hidden group">
                                            {/* Glow Effect */}
                                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-current to-transparent opacity-50" style={{ color: card.color }}></div>
                                            <div className="absolute -top-20 -left-20 w-64 h-64 bg-current rounded-full blur-[100px] opacity-10 pointer-events-none" style={{ color: card.color }}></div>

                                            <div>
                                                <div className="flex items-center gap-4 mb-6">
                                                    <div className="w-24 h-24 flex items-center justify-center">
                                                        <img src={card.icon} alt={card.shortTitle} className="w-full h-full object-contain drop-shadow-[0_0_25px_rgba(255,255,255,0.2)]" />
                                                    </div>
                                                    <div>
                                                        <span className="text-sm font-black text-white tracking-widest uppercase block mb-1">{card.shortTitle}</span>
                                                        <span className="text-[10px] font-bold text-white/60 tracking-widest uppercase bg-white/5 px-2 py-0.5 rounded border border-white/5">{card.tech}</span>
                                                    </div>
                                                </div>

                                                <h3 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                                                    {card.title}
                                                </h3>
                                                
                                                <p className="text-gray-400 text-lg leading-relaxed mb-8">
                                                    {card.desc}
                                                </p>

                                                <div className="flex flex-wrap gap-2 mb-8">
                                                    {card.features.map((feat, i) => (
                                                        <div key={i} className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/5 text-xs text-gray-300 flex items-center gap-2">
                                                            <div className="w-1 h-1 rounded-full bg-current" style={{ color: card.color }} />
                                                            {feat}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            <button 
                                                onClick={() => navigate(card.path)}
                                                className="w-full py-4 rounded-xl font-bold text-sm tracking-widest uppercase transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl group bg-white text-black hover:bg-gray-200"
                                            >
                                                <span>{card.ctaLabel}</span>
                                                <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                                            </button>
                                        </div>

                                        {/* Right Visual */}
                                        <div className="w-full md:w-1/2 bg-[#050505] relative min-h-[300px] md:min-h-full border-l border-white/5">
                                            {/* Visual Component Render */}
                                            {card.visual === 'scan' && <VisualScan color={card.color} image={(card as any).visualImage || card.bgImage} />}
                                            {card.visual === 'layer' && <VisualLayer color={card.color} />}
                                            {card.visual === 'process' && <VisualPixelMaterialize color={card.color} />}
                                            {card.visual === 'blocks' && <VisualBlocks color={card.color} />}
                                            
                                            {/* Step Number */}
                                            <div className="absolute top-6 right-6 text-8xl font-black text-white/5 font-mono select-none pointer-events-none">
                                                {card.step}
                                            </div>
                                        </div>
                                    </motion.div>
                                )
                            ))}
                        </AnimatePresence>
                    </div>

                    {/* Controls */}
                    <div className="flex justify-center mt-12 gap-3">
                        {CARDS.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => handleDotClick(i)}
                                className={`h-1.5 rounded-full transition-all duration-500 ${i === activeIndex ? 'w-12 bg-white' : 'w-2 bg-white/20 hover:bg-white/40'}`}
                            />
                        ))}
                    </div>
                </div>

                {/* --- NOVA SEÇÃO EXPLICATIVA (ECOSSISTEMA) --- */}
                <div className="mt-32 md:mt-48 relative">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-transparent via-cyan-500 to-transparent opacity-50"></div>
                    
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-20"
                    >
                        <h2 className="text-3xl md:text-5xl font-black text-white mb-6">
                            ECOSSISTEMA <span className="text-cyan-400">INTEGRADO</span>
                        </h2>
                        <p className="text-gray-400 max-w-2xl mx-auto text-lg leading-relaxed">
                            Entenda como nossa plataforma une poder de processamento local e inteligência em nuvem para revolucionar sua produção.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
                        {/* Card 1: Launcher */}
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            className="bg-[#111] border border-white/10 rounded-2xl p-8 relative overflow-hidden group hover:border-cyan-500/30 transition-colors"
                        >
                            <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                                <Download size={80} />
                            </div>
                            <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center mb-6 text-cyan-400 group-hover:scale-110 transition-transform">
                                <Cpu size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-3">Launcher Desktop</h3>
                            <p className="text-gray-400 text-sm leading-relaxed mb-4">
                                Para garantir acesso direto ao hardware (GPU) e integração nativa com impressoras, nossas ferramentas rodam exclusivamente via <strong>Launcher Overpixel</strong>.
                            </p>
                            <div className="flex items-center gap-2 text-xs font-bold text-cyan-400">
                                <ShieldCheck size={14} />
                                <span>100% SEGURO & OTIMIZADO</span>
                            </div>
                        </motion.div>

                        {/* Card 2: Assinatura */}
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 }}
                            className="bg-[#111] border border-white/10 rounded-2xl p-8 relative overflow-hidden group hover:border-purple-500/30 transition-colors"
                        >
                            <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                                <Zap size={80} />
                            </div>
                            <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-6 text-purple-400 group-hover:scale-110 transition-transform">
                                <SparklesIcon size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-3">Assinaturas Flexíveis</h3>
                            <p className="text-gray-400 text-sm leading-relaxed mb-4">
                                Cada ferramenta possui sua assinatura individual. Você escolhe exatamente o que precisa para o seu negócio.
                            </p>
                            <ul className="space-y-2">
                                <li className="flex items-center gap-2 text-xs text-gray-300">
                                    <Check size={12} className="text-purple-400" /> Contrate apenas o que usar
                                </li>
                                <li className="flex items-center gap-2 text-xs text-gray-300">
                                    <Check size={12} className="text-purple-400" /> Suporte Prioritário
                                </li>
                            </ul>
                        </motion.div>

                        {/* Card 3: Tokens */}
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.3 }}
                            className="bg-[#111] border border-white/10 rounded-2xl p-8 relative overflow-hidden group hover:border-green-500/30 transition-colors"
                        >
                            <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                                <Coins size={80} />
                            </div>
                            <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-6 text-green-400 group-hover:scale-110 transition-transform">
                                <Coins size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-3">Tokens & Ilimitado</h3>
                            <p className="text-gray-400 text-sm leading-relaxed mb-4">
                                Ferramentas locais (Melhorador Desktop, Montador) são <strong>ILIMITADAS</strong>. Ferramentas de IA Cloud (Melhorador, Factory) usam <strong>Tokens</strong> de baixo custo.
                            </p>
                            <div className="bg-white/5 rounded p-3 border border-white/5">
                                <div className="flex justify-between text-xs mb-1">
                                    <span className="text-gray-300">Melhorador Desktop</span>
                                    <span className="text-green-400 font-bold">ILIMITADO</span>
                                </div>
                                <div className="flex justify-between text-xs">
                                    <span className="text-gray-300">Melhorador Cloud</span>
                                    <span className="text-yellow-400 font-bold">5 TOKENS</span>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* CTA Download */}
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        className="flex flex-col items-center justify-center text-center bg-gradient-to-b from-white/5 to-transparent border border-white/10 rounded-3xl p-12 relative overflow-hidden"
                    >
                        <div className="absolute inset-0 bg-cyan-500/5 blur-3xl rounded-full transform scale-75 animate-pulse"></div>
                        
                        <h3 className="text-2xl md:text-4xl font-black text-white mb-4 relative z-10">
                            PRONTO PARA COMEÇAR?
                        </h3>
                        <p className="text-gray-400 mb-8 max-w-lg relative z-10">
                            Baixe o Launcher Overpixel agora e transforme sua estamparia em uma indústria 4.0.
                        </p>
                        
                        <button 
                            onClick={handleDownload}
                            className="bg-white text-black px-8 py-4 rounded-xl font-black text-lg hover:bg-cyan-400 hover:scale-105 transition-all flex items-center gap-3 shadow-xl shadow-cyan-500/20 relative z-10 group"
                        >
                            <Download size={24} className="group-hover:animate-bounce" />
                            BAIXAR LAUNCHER PARA WINDOWS
                        </button>
                        
                        <div className="mt-4 flex items-center gap-2 text-xs text-gray-500 font-mono relative z-10">
                            <ArrowDown size={12} />
                            <span>VERSÃO {launcherVersion.replace('v', '').toUpperCase()} • WINDOWS 10/11</span>
                        </div>

                        <LoginModal 
                            isOpen={isLoginModalOpen} 
                            onClose={() => setIsLoginModalOpen(false)}
                        />
                    </motion.div>
                </div>

            </div>
        </div>
    )
}
